// Basic
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
import SelectCreatableInput from '@/common-utils/components/ui/selectCreatableInput';
import Select from '@/common-utils/components/ui/select';
import Entity from '@/common-utils/components/renders/entity/Entity';
// Lang
import { nsMerchantUser } from '@/app/i18n/namespaces';
// Enums
import CBVAD_CONTRACT_TYPES from '@/app/modules/accounts/enums/cbVADContractTypes';
import actionsMerchantAccounts from '@/app/modules/accounts/api/actionsMerchantAccounts';

/**
 * @param {function} setValidForm
 * @param {function} setFormData
 * @returns {JSX} AddVadContractForm
 */
function AddVadContractForm({ setValidForm, setFormData }) {
    // Translation
    const { t: translate } = useTranslation();

    // Constants
    const CONTRACT_TYPE_OPTIONS = Object.values(CBVAD_CONTRACT_TYPES);

    // State
    const [x25Options, setX25Options] = useState([]);

    const contractLabelSchema = z.string().refine((value) => !value || value && /^.{0,255}$/.test(value), { message: translate('form.cbvad.contract_label') });
    const contractTypeSchema = z.string().min(1, { message: translate('form.cbvad.contract_type') });
    const contractNumberSchema = z.string().refine((value) => /^.{1,15}$/.test(value), { message: translate('form.cbvad.contract_number') });
    const rankSchema = z.string().refine((value) => /^\d{3}$/.test(value), { message: translate('form.cbvad.rank') });
    const bankCodeSchema = z.string().refine((value) => /^\d{5}$/.test(value), { message: translate('form.cbvad.bank_code') });
    const x25AddressSchema = z.string().refine((value) => value && /^[\d.:]{1,50}$/.test(value), { message: translate('form.cbvad.x25_address') });

    const formSchema = z.object({
        contractLabel: contractLabelSchema,
        contractType: contractTypeSchema,
        contractNumber: contractNumberSchema,
        rank: rankSchema,
        bankCode: bankCodeSchema,
        x25Address: x25AddressSchema
    });

    // Form
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            contractLabel: '',
            contractType: '',
            contractNumber: '',
            rank: '',
            bankCode: '',
            x25Address: ''
        }
    });
    //watch values
    const contractTypeValue = form.watch('contractType');
    const bankCodeValue = form.watch('bankCode');

    //api call
    const { data, refetch } = actionsMerchantAccounts.getX25Centers({ application: contractTypeValue, bankCode: bankCodeValue }, false);
    //hooks
    // Load X25 centres when both application (contract type) and bank code are set
    useEffect(() => {
        const canLoadX25 = contractTypeValue && bankCodeValue && bankCodeValue.length === 5;

        if (!canLoadX25) {
            setX25Options([]);
            return;
        }
        refetch();
    }, [contractTypeValue, bankCodeValue]);

    useEffect(() => {
        if (data) {
            setX25Options(data.x25Centers.map((center) => ({
                name: center,
                value: center
            })));
        }
    }, [data]);

    useEffect(() => {
        if (form.formState.isValid) {
            setValidForm(true);
            setFormData(form.getValues());
        } else {
            setValidForm(false);
        }
    }, [form.formState.isValid, form.getValues()]);

    return (
        <Form {...form}>
            <form className='mx-4 space-y-4'>
                <FormField
                    control={form.control}
                    name='contractType'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.contract_type'
                                />
                            </FormLabel>
                            <FormControl>
                                <Select
                                    options={CONTRACT_TYPE_OPTIONS}
                                    value={field.value}
                                    onChange={field.onChange}
                                    namespace={nsMerchantUser}
                                    prefixEntity='modal.add_external_contract.cbvad.select.contract_type.'
                                    placeholder='modal.add_external_contract.cbvad.select.contract_type.placeholder'
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='bankCode'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.bank_code'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input namespace={nsMerchantUser} entity='' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='contractNumber'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.contract_number'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input
                                    namespace={nsMerchantUser}
                                    entity=''
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='rank'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.rank'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input namespace={nsMerchantUser} entity='' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='x25Address'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.x25_address'
                                />
                            </FormLabel>
                            <FormControl>
                                <SelectCreatableInput
                                    options={x25Options}
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder={<Entity namespace={nsMerchantUser} entity='modal.add_external_contract.cbvad.x25_address' />}
                                    namespace={nsMerchantUser}
                                    inputPattern={/^[\d.:]{1,50}$/}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='contractLabel'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.cbvad.contract_label'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input namespace={nsMerchantUser} entity='' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    );
}

AddVadContractForm.propTypes = {
    setValidForm: PropTypes.func,
    setFormData: PropTypes.func
};

export default AddVadContractForm;